import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';

import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';

import {
	formatBalanceDisplay,
	formatCompactQty,
	formatLastTradePrice,
	formatPrice,
	type BookTicker,
	type Ticker24h,
} from './tradeTheme';

export type { Ticker24h, BookTicker };

type StatTone = 'default' | 'up' | 'down' | 'bid' | 'ask' | 'accent' | 'quote';

const statValueToneCls: Record<StatTone, string> = {
	default: 'text-[#EAECEF]',
	up: 'text-success-dark',
	down: 'text-issue-dark',
	bid: 'text-success-dark',
	ask: 'text-issue-dark',
	accent: 'text-[#38BDF8]',
	quote: 'text-[#F0B90B]',
};

function TickerStat({
	label,
	children,
	tone = 'default',
}: {
	label: string;
	children: React.ReactNode;
	tone?: StatTone;
}) {
	return (
		// 去掉了 min-w-[4.5rem] 限制，改为 shrink-0 以便在 H5 横向滚动时不会被挤压
		<div className="flex shrink-0 flex-col gap-0.5">
			<Text variant="caption/semibold" color="dark" className="whitespace-nowrap text-[#848E9C]">
				{label}
			</Text>
			<Heading
				variant="heading6/semibold"
				className={clsx('whitespace-nowrap tabular-nums', statValueToneCls[tone])}
			>
				{children}
			</Heading>
		</div>
	);
}

function SectionDivider() {
	return (
		// 移动端隐藏分割线，仅在 sm (640px) 以上屏幕显示
		<div
			className="hidden h-9 w-px shrink-0 bg-gradient-to-b from-transparent via-white/15 to-transparent sm:block"
			aria-hidden
		/>
	);
}

function PairHeading({ base, quote }: { base: string; quote: string }) {
	const t = useT();
	return (
		<div className="flex items-baseline gap-1">
			<span className="text-caption font-bold tracking-wide text-[#38BDF8]">{base}</span>
			<span className="text-caption text-[#5E6673]">/</span>
			<span className="text-caption font-bold tracking-wide text-[#F0B90B]">{quote}</span>
			<span className="ml-1.5 rounded border border-white/10 bg-white/5 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-[#848E9C]">
				{t('common.spot')}
			</span>
		</div>
	);
}

export function TradeMarketOverview({
	baseSymbol = 'DGC',
	quoteSymbol = 'DATA',
	displayPrice,
	changeUp,
	klineChange,
	ticker24h,
	bookTicker,
	live,
	cpcBal,
	dataBal,
	showBalances,
}: {
	baseSymbol?: string;
	quoteSymbol?: string;
	displayPrice: string;
	changeUp: boolean;
	klineChange: number | null;
	ticker24h: Ticker24h;
	bookTicker: BookTicker;
	live: boolean;
	cpcBal: string;
	dataBal: string;
	showBalances: boolean;
}) {
	const t = useT();
	const balLabel = (symbol: string) => t('trade.balanceLabel').replace('{symbol}', symbol);
	const changeLabel =
		klineChange != null ? `${changeUp ? '+' : ''}${klineChange.toFixed(2)}%` : '—';
	const change24hLabel =
		ticker24h.changePct != null
			? `${ticker24h.changePct >= 0 ? '+' : ''}${ticker24h.changePct.toFixed(2)}%`
			: '—';
	const spreadLabel =
		bookTicker.spread != null ? formatLastTradePrice(String(bookTicker.spread)) : '—';
	const spreadPctLabel =
		bookTicker.spreadPct != null ? `${bookTicker.spreadPct.toFixed(3)}%` : '—';

	return (
		<Card bg="dark" spacing="sm" growOnHover>
			{/* 最外层在移动端采用纵向 flex-col 布局，PC 端恢复横向 flex-row。
              gap 从 gap-x/gap-y 统一收拢至更适合自适应的 gap-4
            */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">

				{/* 1. 主行情区域：在 H5 端独占一行，并与钱包/余额两端对齐（justify-between） */}
				<div className="flex items-center justify-between gap-3 sm:min-w-[10rem] sm:shrink-0 sm:items-end sm:justify-start">
					<div className="flex flex-col gap-1">
						<PairHeading base={baseSymbol} quote={quoteSymbol} />
						<div className="flex items-center gap-2">
							<Heading
								variant="heading3/semibold"
								className={clsx(
									'tabular-nums tracking-tight text-xl sm:text-2xl', // 缩减移动端价格字号防止溢出
									changeUp ? 'text-success-dark' : 'text-issue-dark',
								)}
							>
								{displayPrice}
							</Heading>
							<span
								className={clsx(
									'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-captionSmall font-semibold',
									live
										? 'bg-success-dark/15 text-success-dark'
										: 'bg-white/5 text-[#848E9C]',
								)}
							>
								<span
									className={clsx(
										'h-1.5 w-1.5 rounded-full',
										live ? 'animate-pulse bg-success-dark' : 'bg-[#5E6673]',
									)}
									aria-hidden
								/>
								{live ? 'Live' : 'Offline'}
							</span>
						</div>
						<div className="flex items-center gap-2">
							<span
								className={clsx(
									'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-captionSmall font-semibold tabular-nums',
									changeUp
										? 'bg-success-dark/15 text-success-dark'
										: 'bg-issue-dark/15 text-issue-dark',
								)}
							>
								<span aria-hidden>{changeUp ? '▲' : '▼'}</span>
								{changeLabel}
							</span>
							<Text variant="caption/semibold" color="dark" className="text-[#5E6673]">
								{t('trade.klineWindow')}
							</Text>
						</div>
					</div>

					{/* 2. 移动端下把“余额/连接状态”提到主行情右侧，充分利用空间；PC 端则隐藏 */}
					<div className="flex flex-col items-end gap-1 sm:hidden">
						{showBalances ? (
							<>
								<div className="text-right">
									<span className="text-[10px] text-[#848E9C]">{balLabel(quoteSymbol)}: </span>
									<span className="text-xs font-semibold text-[#F0B90B] tabular-nums">{formatBalanceDisplay(dataBal)}</span>
								</div>
								<div className="text-right">
									<span className="text-[10px] text-[#848E9C]">{balLabel(baseSymbol)}: </span>
									<span className="text-xs font-semibold text-[#38BDF8] tabular-nums">{formatBalanceDisplay(cpcBal)}</span>
								</div>
							</>
						) : (
							<Text variant="bodySmall/medium" color="dark" className="text-right text-[11px] text-[#848E9C] max-w-[7rem]">
								{t('wallet.viewBalance')}
							</Text>
						)}
					</div>
				</div>

				<SectionDivider />

				{/* 3. 24h / 盘口统计区域：
                  H5 移动端：变成横向可滑动（scrollbar-none / overflow-x-auto），防止多项统计堆叠把页面撑得极高。
                  PC 端：恢复原样，自然 flex-wrap 铺开。
                */}
				<div className="no-scrollbar -mx-3 flex flex-1 items-center gap-x-5 overflow-x-auto px-3 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
					<TickerStat label={t('trade.change24h')} tone={ticker24h.changePct == null || ticker24h.changePct >= 0 ? 'up' : 'down'}>
						{change24hLabel}
					</TickerStat>
					<TickerStat label={t('trade.open24h')}>
						{ticker24h.open != null ? formatPrice(ticker24h.open) : '—'}
					</TickerStat>
					<TickerStat label={t('trade.high24h')} tone="up">
						{ticker24h.high != null ? formatPrice(ticker24h.high) : '—'}
					</TickerStat>
					<TickerStat label={t('trade.low24h')} tone="down">
						{ticker24h.low != null ? formatPrice(ticker24h.low) : '—'}
					</TickerStat>
					<TickerStat label={t('trade.volume24hDgc')}>
						{formatCompactQty(ticker24h.volumeLots)}
					</TickerStat>
					<TickerStat label={`${t('trade.turnover24h')} (${quoteSymbol})`} tone="quote">
						{formatCompactQty(ticker24h.turnoverCpc)}
					</TickerStat>
					<TickerStat label={t('trade.trades24h')}>
						{ticker24h.tradeCount > 0 ? ticker24h.tradeCount.toLocaleString('en-US') : '—'}
					</TickerStat>
					<TickerStat label={t('trade.bestBid')} tone="bid">
						{bookTicker.bestBid ? formatLastTradePrice(bookTicker.bestBid) : '—'}
					</TickerStat>
					<TickerStat label={t('trade.bestAsk')} tone="ask">
						{bookTicker.bestAsk ? formatLastTradePrice(bookTicker.bestAsk) : '—'}
					</TickerStat>
					<TickerStat label={t('common.spread')}>
						<span className="inline-flex items-center">
							{spreadLabel}
							{bookTicker.spread != null && (
								<span className="ml-1 text-[10px] font-medium text-[#848E9C]">
									({spreadPctLabel})
								</span>
							)}
						</span>
					</TickerStat>
				</div>

				<SectionDivider />

				{/* 4. PC 端专属余额展示区域：移动端下直接隐藏（因为已经挪到主行情右侧了） */}
				<div className="hidden shrink-0 items-center gap-x-5 sm:flex">
					{showBalances ? (
						<>
							<TickerStat label={balLabel(quoteSymbol)} tone="quote">
								{formatBalanceDisplay(dataBal)}
							</TickerStat>
							<TickerStat label={balLabel(baseSymbol)} tone="accent">
								{formatBalanceDisplay(cpcBal)}
							</TickerStat>
						</>
					) : (
						<Text variant="bodySmall/medium" color="dark" className="max-w-[12rem] text-[#848E9C]">
							{t('wallet.viewBalance')}
						</Text>
					)}
				</div>
			</div>
		</Card>
	);
}

// 兼容旧 import
export { TradeMarketOverview as TradeTickerBar };
